import React, { useState, useEffect } from 'react';
import api from '../services/api';
import { FiShoppingCart, FiCheckCircle, FiXCircle, FiClock, FiDollarSign, FiAlertTriangle } from 'react-icons/fi';
import { Line } from 'react-chartjs-2';
import { Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend } from 'chart.js';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend);

const Dashboard = () => {
  const [stats, setStats] = useState({});
  const [recentOrders, setRecentOrders] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const [statsRes, ordersRes, alertsRes] = await Promise.all([
        api.get('/dashboard/stats.php'),
        api.get('/dashboard/recent-orders.php'),
        api.get('/dashboard/inventory-alerts.php'),
      ]);

      setStats(statsRes.data.data || {});
      setRecentOrders(ordersRes.data.data || []);
      setAlerts(alertsRes.data.data || []);
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const statCards = [
    { label: 'Total Orders', value: stats.total_orders || 0, icon: FiShoppingCart, color: 'bg-blue-500' },
    { label: 'Pending Orders', value: stats.pending_orders || 0, icon: FiClock, color: 'bg-yellow-500' },
    { label: 'Completed', value: stats.completed_orders || 0, icon: FiCheckCircle, color: 'bg-green-500' },
    { label: 'Cancelled', value: stats.cancelled_orders || 0, icon: FiXCircle, color: 'bg-red-500' },
    { label: 'Revenue', value: `₹${(stats.total_revenue || 0).toLocaleString()}`, icon: FiDollarSign, color: 'bg-purple-500' },
  ];

  if (loading) {
    return <div className="text-center py-20 text-gray-600 dark:text-gray-400">Loading dashboard...</div>;
  }

  return (
    <div className="space-y-6">
      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
        {statCards.map((stat, index) => (
          <div key={index} className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">{stat.label}</p>
                <p className="text-2xl font-bold text-gray-800 dark:text-white">{stat.value}</p>
              </div>
              <div className={`${stat.color} p-3 rounded-lg`}>
                <stat.icon className="w-6 h-6 text-white" />
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Charts and Tables */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Orders */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
          <h3 className="text-lg font-semibold text-gray-800 dark:text-white mb-4">Recent Orders</h3>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 dark:bg-gray-700">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300">Order ID</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300">Customer</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300">Amount</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {recentOrders && recentOrders.length > 0 ? recentOrders.slice(0, 5).map((order) => (
                  <tr key={order.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                    <td className="px-4 py-3 text-sm text-gray-800 dark:text-white">{order.order_number || order.id}</td>
                    <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300">{order.customer_name || 'N/A'}</td>
                    <td className="px-4 py-3 text-sm font-medium text-gray-800 dark:text-white">₹{order.total_amount || 0}</td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                        order.status === 'completed' || order.status === 'delivered' ? 'bg-green-100 text-green-800' :
                        order.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                        order.status === 'cancelled' ? 'bg-red-100 text-red-800' :
                        'bg-blue-100 text-blue-800'
                      }`}>
                        {order.status || 'Unknown'}
                      </span>
                    </td>
                  </tr>
                )) : (
                  <tr>
                    <td colSpan="4" className="px-4 py-8 text-center text-gray-500 dark:text-gray-400">
                      No orders found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Inventory Alerts */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
          <h3 className="text-lg font-semibold text-gray-800 dark:text-white mb-4 flex items-center">
            <FiAlertTriangle className="mr-2 text-yellow-500" />
            Low Stock Alerts
          </h3>
          <div className="space-y-3">
            {alerts && alerts.length > 0 ? alerts.slice(0, 5).map((alert) => (
              <div key={alert.id} className="flex items-center justify-between p-3 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg">
                <div>
                  <p className="text-sm font-medium text-gray-800 dark:text-white">{alert.product_name || alert.name || 'Product'}</p>
                  <p className="text-xs text-gray-600 dark:text-gray-400">SKU: {alert.sku || 'N/A'}</p>
                </div>
                <div className="text-right">
                  <p className="text-sm font-semibold text-red-600 dark:text-red-400">{alert.quantity || 0} left</p>
                  <p className="text-xs text-gray-600 dark:text-gray-400">Reorder: {alert.reorder_level || 0}</p>
                </div>
              </div>
            )) : (
              <p className="text-center text-gray-600 dark:text-gray-400 py-8">No low stock alerts</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
